import { slicedToArray as _slicedToArray, asyncToGenerator as _asyncToGenerator, regeneratorRuntime as _regeneratorRuntime } from './_virtual/_rollupPluginBabelHelpers.mjs';
import { ImpressionArea } from '@toss/impression-area';
import { usePreservedCallback, useBooleanState, useCombinedRefs } from '@toss/react';
import { noop } from '@toss/utils';
import { forwardRef, useRef, useCallback, useState, useEffect, useImperativeHandle } from 'react';
import { AnimationChain } from './AnimationChain.mjs';
import { importLottie } from './importLottie.mjs';
import { jsx } from 'react/jsx-runtime';

var Lottie = /*#__PURE__*/forwardRef(function Lottie(_ref, lottieRef) {
  var className = _ref.className,
      src = _ref.src,
      json = _ref.json,
      assets = _ref.assets,
      _ref$delay = _ref.delay,
      delayMs = _ref$delay === void 0 ? 0 : _ref$delay,
      _ref$speed = _ref.speed,
      speed = _ref$speed === void 0 ? 1 : _ref$speed,
      _ref$loop = _ref.loop,
      loop = _ref$loop === void 0 ? false : _ref$loop,
      _ref$interval = _ref.interval,
      interval = _ref$interval === void 0 ? 0 : _ref$interval,
      _ref$autoPlay = _ref.autoPlay,
      autoPlay = _ref$autoPlay === void 0 ? true : _ref$autoPlay,
      width = _ref.width,
      height = _ref.height,
      alt = _ref.alt,
      _onPlay = _ref.onPlay,
      _onLoopComplete = _ref.onLoopComplete,
      _onComplete = _ref.onComplete;
  var onPlay = usePreservedCallback(_onPlay !== null && _onPlay !== void 0 ? _onPlay : noop);
  var onLoopComplete = usePreservedCallback(_onLoopComplete !== null && _onLoopComplete !== void 0 ? _onLoopComplete : noop);
  var onComplete = usePreservedCallback(_onComplete !== null && _onComplete !== void 0 ? _onComplete : noop);
  var containerRef = useRef(null);
  var animationChainRef = useRef(null);

  var _useBooleanState = useBooleanState(false),
      _useBooleanState2 = _slicedToArray(_useBooleanState, 2),
      isVisible = _useBooleanState2[0],
      handleImpressionStart = _useBooleanState2[1];

  var isFirstPlayRef = useRef(true);
  var fetchAnimationChain = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
    var lottie, animationData, chain;
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            if (!(containerRef.current == null)) {
              _context.next = 2;
              break;
            }

            return _context.abrupt("return", null);

          case 2:
            _context.next = 4;
            return importLottie();

          case 4:
            lottie = _context.sent;
            animationData = typeof json === 'string' ? [{
              json: json,
              assets: assets
            }] : typeof src === 'string' ? [{
              url: src,
              assets: assets
            }] : src.map(function (url) {
              return {
                url: url,
                assets: assets
              };
            });
            chain = AnimationChain(animationData, {
              loop: loop,
              interval: interval,
              container: containerRef.current,
              lottie: lottie
            });
            _context.next = 9;
            return chain.prepare();

          case 9:
            return _context.abrupt("return", chain);

          case 10:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), [json, assets, src, loop, interval]);

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      animationChain = _useState2[0],
      setAnimationChain = _useState2[1];

  useEffect(function () {
    var isRunning = true;
    fetchAnimationChain().then(function (chain) {
      if (isRunning) {
        setAnimationChain(chain);
      }
    });
    return function () {
      isRunning = false;
    };
  }, [fetchAnimationChain]);
  var handleContainerChange = useCallback(function (element) {
    if (isInViewport(element)) {
      handleImpressionStart();
    }
  }, [handleImpressionStart]);
  var startAnimation = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2() {
    return _regeneratorRuntime().wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            if (!(animationChain == null)) {
              _context2.next = 2;
              break;
            }

            return _context2.abrupt("return");

          case 2:
            if (!(isFirstPlayRef.current && delayMs > 0)) {
              _context2.next = 6;
              break;
            }

            _context2.next = 5;
            return delay(delayMs);

          case 5:
            isFirstPlayRef.current = false;

          case 6:
            animationChain.start({
              speed: speed
            });
            onPlay === null || onPlay === void 0 ? void 0 : onPlay();

          case 8:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  })), [animationChain, delayMs, speed, onPlay]);
  var stopAnimation = useCallback(function () {
    animationChain === null || animationChain === void 0 ? void 0 : animationChain.destroy();
  }, [animationChain]);
  var playAnimation = useCallback(function () {
    animationChain === null || animationChain === void 0 ? void 0 : animationChain.play();
  }, [animationChain]);
  var pauseAnimation = useCallback(function () {
    animationChain === null || animationChain === void 0 ? void 0 : animationChain.pause();
  }, [animationChain]);
  useImperativeHandle(lottieRef, function () {
    return {
      start: startAnimation,
      stop: stopAnimation,
      play: playAnimation,
      pause: pauseAnimation
    };
  }, [startAnimation, stopAnimation, playAnimation, pauseAnimation]);
  useEffect(function () {
    animationChain === null || animationChain === void 0 ? void 0 : animationChain.showFirstFrame();
  }, [animationChain]);
  useEffect(function () {
    onComplete && (animationChain === null || animationChain === void 0 ? void 0 : animationChain.addEventListener('complete', onComplete));
    onLoopComplete && (animationChain === null || animationChain === void 0 ? void 0 : animationChain.addEventListener('loopComplete', onLoopComplete));
    return function () {
      onComplete && (animationChain === null || animationChain === void 0 ? void 0 : animationChain.removeEventListener('complete', onComplete));
      onLoopComplete && (animationChain === null || animationChain === void 0 ? void 0 : animationChain.removeEventListener('loopComplete', onLoopComplete));
    };
  }, [animationChain, onLoopComplete, onComplete]);
  useEffect(function () {
    if (!isVisible) {
      return;
    }

    if (!autoPlay) {
      return;
    }

    startAnimation();
    return function () {
      animationChain === null || animationChain === void 0 ? void 0 : animationChain.destroy();
    };
  }, [animationChain, autoPlay, isVisible, startAnimation]);
  useEffect(function () {
    var _animationChainRef$cu;

    (_animationChainRef$cu = animationChainRef.current) === null || _animationChainRef$cu === void 0 ? void 0 : _animationChainRef$cu.setSpeed(speed);
  }, [speed]);
  var ref = useCombinedRefs(containerRef, handleContainerChange);
  return /*#__PURE__*/jsx(ImpressionArea, {
    style: {
      width: width,
      height: height
    },
    className: className,
    ref: ref,
    onImpressionStart: handleImpressionStart,
    role: alt != null ? 'img' : undefined,
    "aria-label": alt,
    "aria-hidden": alt == null
  }, "".concat(loop, "-").concat(interval, "-").concat(src, "-").concat(json));
});

function delay(ms) {
  return new Promise(function (resolve) {
    window.setTimeout(resolve, ms);
  });
}

function isInViewport(element) {
  var _window$innerWidth, _window$innerHeight;

  if (element == null) {
    return false;
  }

  var rect = element.getBoundingClientRect();
  var windowWidth = (_window$innerWidth = window.innerWidth) !== null && _window$innerWidth !== void 0 ? _window$innerWidth : document.documentElement.clientWidth;
  var windowHeight = (_window$innerHeight = window.innerHeight) !== null && _window$innerHeight !== void 0 ? _window$innerHeight : document.documentElement.clientHeight;
  return rect.top <= windowHeight && rect.bottom >= 0 && rect.left <= windowWidth && rect.right >= 0;
}

export { Lottie };
